import { useCallback, useEffect, useMemo, useState } from "react";
import { bridgeReadiness, relativeHeartbeat } from "../bridgeHealth";
import { Icon } from "../icons";
import { listLocalBridgeConnections, type LocalBridgeConnection } from "../localBridgeApi";
import type { AuthSession } from "../types";

type Notify = (tone: "success" | "warning" | "danger", text: string) => void;

export function LocalBridgeChannelsPanel({ organizationName, session, notify }: {
  organizationName: string;
  session: AuthSession;
  notify: Notify;
}) {
  const [connections, setConnections] = useState<LocalBridgeConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const healthy = useMemo(() => connections.filter((item) => bridgeReadiness(item).tone === "ready").length, [connections]);

  const load = useCallback(async (showError = false) => {
    try {
      setConnections(await listLocalBridgeConnections(session));
    } catch (cause) {
      if (showError) notify("warning", `No se pudo verificar el estado de los chips: ${errorMessage(cause)}`);
    } finally {
      setLoading(false);
    }
  }, [session, notify]);

  useEffect(() => {
    let active = true;
    void load(true);
    const timer = window.setInterval(() => active && void load(false), 15_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [load]);

  return <section className="channel-section bridge-channel-section">
    <div className="channel-section-heading">
      <span className="connection-icon"><Icon name="whatsapp" size={20} /></span>
      <div><p className="section-kicker">CHIPS PARA WHATSAPP</p><h2>Números físicos y grupos</h2><p>Cada chip pertenece únicamente a {organizationName} y mantiene una sesión independiente.</p></div>
      <div className="bridge-health-summary"><strong>{healthy}/{connections.length}</strong><span>funcionando</span></div>
    </div>

    {loading ? <section className="card-panel admin-panel"><div className="friendly-empty"><strong>Verificando chips…</strong><p>Consultando el último pulso registrado en AWS.</p></div></section> : null}
    {!loading && !connections.length ? <section className="card-panel admin-panel bridge-empty-panel">
      <div className="friendly-empty"><span><Icon name="whatsapp" size={24} /></span><strong>Aún no hay un chip preparado para {organizationName}</strong><p>Crea una conexión ahora; cuando tengas el teléfono, sólo tendrás que escanear el QR.</p><a className="button primary" href="/local-bridge.html"><Icon name="plus" size={16} /> Preparar chip</a></div>
    </section> : null}

    {connections.length ? <div className="bridge-connection-grid">
      {connections.map((connection) => {
        const readiness = bridgeReadiness(connection);
        const captured = connection.metrics?.capturedMessages ?? 0;
        const enabled = connection.captureMode === "resident_allowlist" ? connection.metrics?.enabledResidents ?? 0 : connection.metrics?.enabledGroups ?? 0;
        return <article className={`card-panel bridge-connection-card ${readiness.tone}`} key={connection.connectionId}>
          <header><div><span className="bridge-phone-icon"><Icon name="whatsapp" size={19} /></span><div><strong>{connection.label}</strong><small>{connection.phone ? `+${connection.phone}` : "Número pendiente de QR"}</small></div></div><span className={`bridge-readiness-chip ${readiness.tone}`}><i />{readiness.label}</span></header>
          <p className="bridge-readiness-detail">{readiness.detail}</p>
          <dl className="bridge-health-metrics">
            <div><dt>Último pulso</dt><dd>{relativeHeartbeat(connection.lastHeartbeatAt)}</dd></div>
            <div><dt>Uso</dt><dd>{connection.captureMode === "resident_allowlist" ? "PaseFirme · residentes" : "Grupos convencionales"}</dd></div>
            <div><dt>{connection.captureMode === "resident_allowlist" ? "Residentes" : "Grupos habilitados"}</dt><dd>{enabled}</dd></div>
            <div><dt>Mensajes capturados</dt><dd>{captured.toLocaleString("es-CL")}</dd></div>
          </dl>
          {connection.lastError && readiness.tone !== "ready" ? <div className="bridge-card-warning"><strong>Último aviso</strong><span>{connection.lastError}</span></div> : null}
          <footer><span>{connection.host || connection.deviceLabel}</span><a className="button soft compact" href="/local-bridge.html">Administrar</a></footer>
        </article>;
      })}
    </div> : null}

    <div className="admin-inline-note bridge-help-note"><Icon name="check" size={16} /><span>Un chip está operativo cuando indica <strong>Funcionando</strong>, tiene un pulso reciente y muestra el número vinculado.</span><a href="/local-bridge.html">Agregar o revisar chips</a></div>
  </section>;
}

function errorMessage(cause: unknown) {
  return (cause instanceof Error ? cause.message : "ERROR_DESCONOCIDO").replaceAll("_", " ").toLocaleLowerCase("es-CL");
}
